import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Box, Typography, IconButton, Paper, Chip } from "@mui/material";
import CloseIcon from '@mui/icons-material/Close';
import { useEffect, useState } from "react";
import { useUser } from "../../context/userContext";
import { createLog } from "../../api/logApi";
import { updateCommissionProgress } from "../../api/commissionProgressApi";
import { getSlipsByAgent } from "../../api/commissionSlipApi";
import { convertDateStringToDateTime } from "../../utils/datetimeUtils";
import LogViewer from "../common/logViewer";
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { handlePriceWithRM } from "../../utils/priceUtils";

// Currency for figures that may legitimately be NEGATIVE. A DOC/TOC row whose Upline/Team
// discount exceeds the recipient's override is a correct −RM row (spec §8.3) and must never
// be clamped. Rounds to 2 dp first so a float residue can never print as "- RM 0.00"; the
// "- RM x" sign convention comes from handlePriceWithRM.
const rm = (v) => {
    const n = Math.round((Number(v) || 0) * 100) / 100;
    return handlePriceWithRM(n === 0 ? 0 : n);
};

export default function EditSalesCommissionModal({ show, onHide, selectedCommission, onSaved, fullPage = false }) {
    const { user } = useUser();
    const commPay = parseFloat(selectedCommission?.commissionProgressDto?.commPay) || 0;
    const isCompleted = selectedCommission?.commissionProgressDto?.paymentStatus === "Completed";

    const [firstPayment, setFirstPayment] = useState('');
    const [firstPaymentOn, setFirstPaymentOn] = useState("");
    const [secondPayment, setSecondPayment] = useState('');
    const [secondPaymentOn, setSecondPaymentOn] = useState("");
    const [repayments, setRepayments] = useState([
        { amount: '', date: '' },
        { amount: '', date: '' },
        { amount: '', date: '' },
        { amount: '', date: '' },
        { amount: '', date: '' },
    ]);
    const [remarks, setRemarks] = useState("");
    const [approvedPeriods, setApprovedPeriods] = useState([]);
    const [approvedSlipData, setApprovedSlipData] = useState([]);
    // Slip items for this sale tagged with their slip's status — drives the pay-row chips.
    const [slipItems, setSlipItems] = useState([]);

    const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    };

    const isDateBeforeToday = (dateString) => {
        if (!dateString) return false;
        const today = new Date(); today.setHours(0, 0, 0, 0);
        const inputDate = new Date(dateString); inputDate.setHours(0, 0, 0, 0);
        return inputDate < today;
    };

    // Determine which period a date falls in (format: "22 Mon - 21 Mon YYYY")
    const getSlipPeriod = (dateStr) => {
        if (!dateStr) return null;
        const d = new Date(dateStr);
        const day = d.getDate();
        let startMonth, startYear, endMonth, endYear;
        if (day >= 22) {
            startMonth = d.getMonth(); startYear = d.getFullYear();
            endMonth = d.getMonth() + 1; endYear = d.getFullYear();
            if (endMonth > 11) { endMonth = 0; endYear++; }
        } else {
            endMonth = d.getMonth(); endYear = d.getFullYear();
            startMonth = d.getMonth() - 1; startYear = d.getFullYear();
            if (startMonth < 0) { startMonth = 11; startYear--; }
        }
        const fmt = (m, y, day) => `${String(day).padStart(2, '0')} ${new Date(y, m, 1).toLocaleDateString('en-GB', { month: 'short' })}`;
        return `${fmt(startMonth, startYear, 22)} - ${fmt(endMonth, endYear, 21)} ${endYear}`;
    };

    const isDateInApprovedPeriod = (dateStr) => {
        const period = getSlipPeriod(dateStr);
        return period && approvedPeriods.includes(period);
    };

    useEffect(() => {
        if (show && selectedCommission) {
            const commission = selectedCommission.commissionProgressDto;
            setFirstPayment(commission.firstPayment ?? '');
            setFirstPaymentOn(formatDateForInput(commission.firstPaymentOn));
            setSecondPayment(commission.secondPayment ?? '');
            setSecondPaymentOn(formatDateForInput(commission.secondPaymentOn));
            setRepayments([
                { amount: commission.repayment ?? '', date: formatDateForInput(commission.repaymentOn) },
                { amount: commission.repayment2 ?? '', date: formatDateForInput(commission.repayment2On) },
                { amount: commission.repayment3 ?? '', date: formatDateForInput(commission.repayment3On) },
                { amount: commission.repayment4 ?? '', date: formatDateForInput(commission.repayment4On) },
                { amount: commission.repayment5 ?? '', date: formatDateForInput(commission.repayment5On) },
            ]);
            setRemarks("");

            // Fetch approved slips for this agent
            const agentId = commission.paymentTo;
            if (agentId) {
                getSlipsByAgent(agentId)
                    .then(res => {
                        const slips = res.data || [];
                        const approvedSlips = slips.filter(s => s.status === 'Approved');
                        setApprovedPeriods(approvedSlips.map(s => s.period));

                        // Parse slip data to know exactly which payments sit on which slip.
                        // Approved items drive the field LOCK (unchanged); the tagged list
                        // below adds Pending too, so each pay row can show whether it has
                        // actually been paid out. (2026-08-11)
                        const allApprovedItems = [];
                        const tagged = [];
                        slips.forEach(s => {
                            if (s.status !== 'Approved' && s.status !== 'Pending') return;
                            try {
                                const items = JSON.parse(s.slipData || '[]');
                                items.forEach(item => {
                                    if (item.salesId !== commission.salesId) return;
                                    // Lock list: unchanged from before — no type filter.
                                    if (s.status === 'Approved') allApprovedItems.push(item);
                                    // Status list: this modal edits ONE CommPayType, so another
                                    // type's row must not colour this one's chip.
                                    if (item.type && commission.commPayType && item.type !== commission.commPayType) return;
                                    tagged.push({ ...item, _status: s.status, _period: s.period });
                                });
                            } catch {}
                        });
                        setApprovedSlipData(allApprovedItems);
                        setSlipItems(tagged);
                    })
                    .catch(() => { setApprovedPeriods([]); setApprovedSlipData([]); setSlipItems([]); });
            }
        } else if (!show) {
            setFirstPayment(''); setFirstPaymentOn("");
            setSecondPayment(''); setSecondPaymentOn("");
            setRepayments([
                { amount: '', date: '' },
                { amount: '', date: '' },
                { amount: '', date: '' },
                { amount: '', date: '' },
                { amount: '', date: '' },
            ]);
            setRemarks("");
            setApprovedPeriods([]);
            setApprovedSlipData([]);
            setSlipItems([]);
        }
    }, [show, selectedCommission, commPay]);

    const commType = selectedCommission?.commissionProgressDto?.commPayType || '';
    const isTotalComm = commType === 'Total Commission' || commType === 'Webbey Commission';

    // Lock based on SAVED values (from DB), not current input
    const saved = selectedCommission?.commissionProgressDto || {};
    const savedHasValue = (val, date) => date && (parseFloat(val) || 0) !== 0;

    // Payout status per pay row, read off the slips this payment actually landed on:
    //   Approved → the money has been paid out (slip approved)
    //   Pending  → a slip exists but is still awaiting approval
    //   No Slip  → entered here, but no slip generated for it yet
    // Matches on the slip snapshot's own field value, same basis as the lock. (2026-08-11)
    const slipStatusFor = (fieldName, val) => {
        const amt = parseFloat(val) || 0;
        if (!amt) return null;
        const onSlip = (status) => slipItems.some(it =>
            it._status === status && it[fieldName] != null &&
            Math.abs((it[fieldName] || 0) - amt) < 0.01);
        if (onSlip('Approved')) return { label: 'Approved', color: '#047857', bg: '#ECFDF5', border: '#A7F3D0' };
        if (onSlip('Pending')) return { label: 'Pending', color: '#B45309', bg: '#FFFBEB', border: '#FDE68A' };
        return { label: 'No Slip', color: '#64748B', bg: '#F1F5F9', border: '#E2E8F0' };
    };

    // Check if a specific payment value exists in any approved slip's snapshot
    const isOnApprovedSlip = (fieldName, val) => {
        if (!val || !approvedSlipData.length) return false;
        return approvedSlipData.some(item => item[fieldName] != null && Math.abs((item[fieldName] || 0) - (parseFloat(val) || 0)) < 0.01);
    };

    // For PSC/DOC/TOC: lock only if the value is on an approved slip snapshot
    // For Total Comm/Webbey: lock once saved (has value + date)
    const firstPaymentLocked = isTotalComm
        ? savedHasValue(saved.firstPayment, saved.firstPaymentOn)
        : isOnApprovedSlip('firstPayment', saved.firstPayment);
    const secondPaymentLocked = isTotalComm
        ? savedHasValue(saved.secondPayment, saved.secondPaymentOn)
        : isOnApprovedSlip('secondPayment', saved.secondPayment);
    const repaymentFieldNames = ['repayment', 'repayment2', 'repayment3', 'repayment4', 'repayment5'];
    const savedRepayments = [
        { amount: saved.repayment, date: saved.repaymentOn },
        { amount: saved.repayment2, date: saved.repayment2On },
        { amount: saved.repayment3, date: saved.repayment3On },
        { amount: saved.repayment4, date: saved.repayment4On },
        { amount: saved.repayment5, date: saved.repayment5On },
    ];
    const repaymentLocks = savedRepayments.map((sr, idx) =>
        isTotalComm
            ? savedHasValue(sr.amount, sr.date)
            : isOnApprovedSlip(repaymentFieldNames[idx], sr.amount)
    );

    const firstPaymentEnabled = !firstPaymentLocked;
    const secondPaymentEnabled = !secondPaymentLocked;

    // Calculate net progress including all repayments
    const totalRepayments = repayments.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    const totalAllPaid = (parseFloat(firstPayment) || 0) + (parseFloat(secondPayment) || 0) + totalRepayments;
    // `!== 0`, not `> 0`: a NEGATIVE commission row (Upline/Team discount exceeded the
    // override) still has real progress — paid and target carry the same sign, so the ratio
    // is meaningful. A `> 0` test would peg it at 0% forever and keep the repayment rows
    // permanently visible. (spec §8.3)
    const netProgress = commPay !== 0 ? Math.round((totalAllPaid / commPay) * 10000) / 100 : 0;

    const updateRepayment = (idx, field, value) => {
        setRepayments(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    };

    // Do not auto-fill 2nd payment — manual entry only

    // Repayment is manual only — do not auto-calculate or clear

    const repaymentLabels = ['3rd', '4th', '5th', '6th', '7th'];
    const validatePayments = () => {
        // Only validate that if an amount is entered, a date must be provided
        if ((parseFloat(firstPayment) || 0) !== 0 && !firstPaymentOn) { alert("Please provide a date for 1st Pay."); return false; }
        if ((parseFloat(secondPayment) || 0) !== 0 && !secondPaymentOn) { alert("Please provide a date for 2nd Pay."); return false; }
        for (let i = 0; i < repayments.length; i++) {
            if ((parseFloat(repayments[i].amount) || 0) !== 0 && !repayments[i].date) {
                alert(`Please provide a date for ${repaymentLabels[i]} Pay.`);
                return false;
            }
        }
        return true;
    };

    const handleSaveChange = async () => {
        if (!validatePayments()) return;
        const commissionData = {
            commProgressId: selectedCommission.commProgressId,
            salesId: selectedCommission.commissionProgressDto.salesId,
            paymentTo: selectedCommission.commissionProgressDto.paymentTo,
            firstPayment: parseFloat(firstPayment) || null,
            firstPaymentOn: firstPaymentOn ? convertDateStringToDateTime(firstPaymentOn) : null,
            secondPayment: parseFloat(secondPayment) || null,
            secondPaymentOn: secondPaymentOn ? convertDateStringToDateTime(secondPaymentOn) : null,
            repayment: parseFloat(repayments[0].amount) || null,
            repaymentOn: repayments[0].date ? convertDateStringToDateTime(repayments[0].date) : null,
            repayment2: parseFloat(repayments[1].amount) || null,
            repayment2On: repayments[1].date ? convertDateStringToDateTime(repayments[1].date) : null,
            repayment3: parseFloat(repayments[2].amount) || null,
            repayment3On: repayments[2].date ? convertDateStringToDateTime(repayments[2].date) : null,
            repayment4: parseFloat(repayments[3].amount) || null,
            repayment4On: repayments[3].date ? convertDateStringToDateTime(repayments[3].date) : null,
            repayment5: parseFloat(repayments[4].amount) || null,
            repayment5On: repayments[4].date ? convertDateStringToDateTime(repayments[4].date) : null,
            updatedBy: user.username
        };
        try {
            const res = await updateCommissionProgress(commissionData);
            if (res.status === 200) {
                try { await createLog({ module: "Commission Progress", recordId: selectedCommission.commProgressId, action: "Update", oldValue: JSON.stringify(selectedCommission), newValue: JSON.stringify(res.data), remarks, createdBy: user.username ?? "" }); }
                catch (e) { console.error("Failed to create log:", e); }
                onSaved?.();
                setRemarks("");
                if (!fullPage) onHide();
                alert("Commission progress updated successfully.");
            }
        } catch (e) {
            console.error("Failed to update:", e);
            // The API deliberately returns the REAL reason as { message } — most often
            // "Period X already has an APPROVED commission slip — that round is closed."
            // Swallowing it and printing "please try again" sent accountants into a retry
            // loop on the one thing retrying can never fix. (user 2026-08-24)
            const msg = e?.response?.data?.message
                     || e?.response?.data?.title
                     || (typeof e?.response?.data === "string" ? e.response.data : null);
            alert(msg || "Failed to update. Please try again.");
        }
    };

    if (!show) return null;

    const totalPayments = (parseFloat(firstPayment) || 0) + (parseFloat(secondPayment) || 0);
    const allRepaySum = repayments.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
    const totalPaid = totalPayments + allRepaySum;
    const remaining = Math.round((commPay - totalPaid) * 100) / 100;
    // `!== 0` for the same reason as netProgress above — never floor a negative row to 0%.
    const percentage = commPay !== 0 ? Math.round((totalPaid / commPay) * 100 * 100) / 100 : 0;
    const sd = selectedCommission?.salesDetailsForCommissionProgress;
    const cd = selectedCommission?.commissionProgressDto;
    const commRate = sd?.finalPrice > 0 ? Math.round((commPay / sd.finalPrice) * 100 * 100) / 100 : 0;

    const sectionTitle = (text) => (
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mt: 2.5, mb: 1, pb: 0.5, borderBottom: '1px solid #E2E8F0', fontFamily: "'Poppins', sans-serif" }}>
            {text}
        </Typography>
    );

    const firstPaymentSuggested = Math.round(commPay * 0.6 * 100) / 100;
    const actualRemaining = Math.round((commPay - (parseFloat(firstPayment) || 0)) * 100) / 100;
    const anyLocked = firstPaymentLocked || secondPaymentLocked || repaymentLocks.some(Boolean);

    const paymentSection = (title, amount, setAmount, date, setDate, enabled, helperText, status) => (
        <>
            {status ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mt: 2.5, mb: 1, pb: 0.5, borderBottom: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: "'Poppins', sans-serif" }}>
                        {title}
                    </Typography>
                    <Chip label={status.label} size="small"
                        sx={{ height: 20, fontSize: '0.66rem', fontWeight: 600, fontFamily: "'Poppins', sans-serif", backgroundColor: status.bg, color: status.color, border: `1px solid ${status.border}` }} />
                </Box>
            ) : sectionTitle(title)}
            {helperText && <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontStyle: 'italic', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>{helperText}</Typography>}
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                <TextField label="Payout" type="number" size="small" fullWidth disabled={!enabled}
                    value={amount} onChange={e => setAmount(e.target.value)}
                    onWheel={e => e.target.blur()}
                    placeholder={amount === '' || amount === 0 || amount === null ? 'Enter amount' : ''}
                    InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
                <DatePicker label="Payout Date" format="DD/MM/YYYY" disabled={!enabled}
                    value={date ? dayjs(date) : null} onChange={(val) => setDate(val ? val.format('YYYY-MM-DD') : '')}
                    slotProps={{ textField: { size: 'small', fullWidth: true, InputProps: { sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } } } }} />
            </Box>
        </>
    );

    const formContent = (
            <Box>
                {/* Summary Cards */}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)' }, gap: 1.5, mb: 2 }}>
                    {[
                        // Negative totals are legitimate (Upline/Team discount > this recipient's
                        // override) and are shown in red rather than clamped or hidden.
                        { label: 'Total Commission', value: rm(commPay), bg: '#F8FAFC', color: commPay < 0 ? '#DC2626' : '#0F172A' },
                        { label: 'Paid', value: rm(totalPaid), bg: '#ECFDF5', color: totalPaid < 0 ? '#DC2626' : '#10B981' },
                        { label: 'Remaining', value: rm(remaining), bg: '#FFF7ED', color: remaining < 0 ? '#DC2626' : '#F59E0B' },
                        { label: 'Progress', value: `${percentage}%`, bg: '#EFF6FF', color: '#1A8CFF' },
                    ].map(card => (
                        <Box key={card.label} sx={{ background: card.bg, borderRadius: '10px', padding: '10px 14px' }}>
                            <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#94A3B8', mb: 0.5, fontFamily: "'Poppins', sans-serif" }}>
                                {card.label}
                            </Typography>
                            <Typography sx={{ fontSize: '1rem', fontWeight: 700, color: card.color, fontFamily: "'Poppins', sans-serif" }}>
                                {card.value}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Commission Calculation Breakdown */}
                <Box sx={{ background: '#F8FAFC', borderRadius: '10px', padding: '12px 16px', mb: 2, border: '1px solid #E2E8F0' }}>
                    <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', mb: 1, fontFamily: "'Poppins', sans-serif" }}>
                        Commission Calculation
                    </Typography>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: "'Poppins', sans-serif" }}>
                                Type: <strong>{cd?.commPayType ?? '—'}</strong>
                            </Typography>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: "'Poppins', sans-serif" }}>
                                Payment To: <strong>{cd?.paymentToName ?? '—'}</strong>
                            </Typography>
                        </Box>
                        <Box sx={{ borderTop: '1px dashed #E2E8F0', pt: 0.5, mt: 0.5 }}>
                            <Typography sx={{ fontSize: '0.8rem', color: '#374151', fontFamily: "'Poppins', sans-serif" }}>
                                Final Price: <strong>RM {(sd?.finalPrice || 0).toLocaleString()}</strong>
                                {' × '} Rate: <strong>{commRate}%</strong>
                                {' = '} <strong style={{ color: commPay < 0 ? '#DC2626' : '#1A8CFF' }}>{rm(commPay)}</strong>
                            </Typography>
                        </Box>
                        {sd?.originalPrice && sd.originalPrice !== sd.finalPrice && (
                            <Typography sx={{ fontSize: '0.72rem', color: '#94A3B8', fontFamily: "'Poppins', sans-serif" }}>
                                Ori Price RM {sd.originalPrice.toLocaleString()}
                                {sd.discountByAstern ? ` − Astern Disc RM ${sd.discountByAstern.toLocaleString()}` : ''}
                                {sd.discountByAgent ? ` − Agent Disc RM ${sd.discountByAgent.toLocaleString()}` : ''}
                                {/* Upline / Team discount — same line, same pattern. They are real
                                    price cuts funded by the upline(s) / leader(s). (spec §1) */}
                                {sd.discountByUpline ? ` − Upline Disc RM ${sd.discountByUpline.toLocaleString()}` : ''}
                                {sd.discountByTeam ? ` − Team Disc RM ${sd.discountByTeam.toLocaleString()}` : ''}
                                {' = '}Final RM {sd.finalPrice.toLocaleString()}
                            </Typography>
                        )}
                    </Box>
                </Box>

                {/* Sales Info */}
                {sectionTitle('Sales Information')}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField label="Agent Name" size="small" fullWidth disabled value={sd?.agentName ?? ''} InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
                    <TextField label="Customer Name" size="small" fullWidth disabled value={sd?.customerName ?? ''} InputProps={{ sx: { fontFamily: "'Poppins', sans-serif", fontSize: '0.85rem' } }} />
                </Box>

                {/* Payment Sections */}
                {paymentSection('1st Pay', firstPayment, setFirstPayment, firstPaymentOn, setFirstPaymentOn, firstPaymentEnabled, `60% of ${rm(commPay)} = ${rm(firstPaymentSuggested)}`, slipStatusFor('firstPayment', saved.firstPayment))}
                {paymentSection('2nd Pay', secondPayment, setSecondPayment, secondPaymentOn, setSecondPaymentOn, secondPaymentEnabled, `Remaining amount = ${rm(actualRemaining)}`, slipStatusFor('secondPayment', saved.secondPayment))}
                {repayments.map((rep, idx) => {
                    const prevFilled = idx === 0 || (repayments[idx - 1].amount && repayments[idx - 1].date);
                    const thisHasValue = rep.amount || rep.date;
                    const show = idx === 0 || (prevFilled && (netProgress !== 100 || thisHasValue));
                    if (!show) return null;

                    const label = repaymentLabels[idx] + ' Pay';
                    const locked = repaymentLocks[idx];

                    // Calculate paid before this pay
                    const paidBefore = (parseFloat(firstPayment) || 0) + (parseFloat(secondPayment) || 0)
                        + repayments.slice(0, idx).reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0);
                    const diff = Math.round((paidBefore - commPay) * 100) / 100;
                    let helperText;
                    if (idx === 0) {
                        helperText = diff > 0
                            ? `Overpaid by RM ${diff.toFixed(2)} — enter negative to settle (e.g. -${diff.toFixed(2)})`
                            : diff < 0
                            ? `Remaining amount = RM ${Math.abs(diff).toFixed(2)}`
                            : totalPaid > 0 ? 'Fully paid — no additional pay needed' : null;
                    } else {
                        const prevLabel = repaymentLabels[idx - 1];
                        helperText = diff > 0
                            ? `Still overpaid by RM ${diff.toFixed(2)} after ${prevLabel} Pay`
                            : diff < 0
                            ? `Still underpaid by RM ${Math.abs(diff).toFixed(2)} after ${prevLabel} Pay`
                            : null;
                    }

                    return <Box key={idx}>{paymentSection(label, rep.amount, (v) => updateRepayment(idx, 'amount', v), rep.date, (v) => updateRepayment(idx, 'date', v), !locked, helperText, slipStatusFor(repaymentFieldNames[idx], savedRepayments[idx].amount))}</Box>;
                })}
                {anyLocked && (
                    <Typography sx={{ fontSize: '0.72rem', color: '#F59E0B', fontStyle: 'italic', mt: 1, fontFamily: "'Poppins', sans-serif" }}>
                        Fields with approved commission slips are locked. Only unapproved period payments can be edited.
                    </Typography>
                )}

                {/* Remarks & Log */}
                <Box sx={{ mt: 2 }}>
                    <LogViewer module="Commission Progress" recordId={selectedCommission?.commProgressId} remarks={remarks} setRemarks={setRemarks} />
                </Box>
            </Box>
    );

    const actionButtons = (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 2, p: fullPage ? 0 : 2 }}>
            <Button onClick={onHide} sx={{ fontFamily: "'Poppins', sans-serif", textTransform: 'none', color: '#64748B' }}>Cancel</Button>
            <Button onClick={handleSaveChange} variant="contained"
                sx={{ background: 'linear-gradient(135deg, #1A8CFF, #0F62CC)', fontFamily: "'Poppins', sans-serif", fontWeight: 600, textTransform: 'none', borderRadius: '8px', boxShadow: 'none', '&:hover': { background: 'linear-gradient(135deg, #0F62CC, #0A4FA3)', boxShadow: 'none' } }}>
                Save Changes
            </Button>
        </Box>
    );

    if (fullPage) {
        return (
            <Paper elevation={0} sx={{ p: 3, borderRadius: '14px', border: '1px solid #E2E8F0' }}>
                {formContent}
                {actionButtons}
            </Paper>
        );
    }

    return (
        <Dialog open={show} onClose={onHide} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: '14px' } }}>
            <DialogTitle sx={{ fontFamily: "'Poppins', sans-serif", fontWeight: 700, fontSize: '1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1 }}>
                <span>
                    Commission: RM {commPay.toFixed(2)}
                    <Typography component="span" sx={{ fontSize: '0.8rem', color: '#64748B', ml: 1, fontFamily: "'Poppins', sans-serif" }}>
                        ({sd?.customerName})
                    </Typography>
                </span>
                <IconButton onClick={onHide} size="small"><CloseIcon fontSize="small" /></IconButton>
            </DialogTitle>
            <DialogContent sx={{ pt: '8px !important' }}>
                {formContent}
            </DialogContent>
            {actionButtons}
        </Dialog>
    );
}
